import React, { useEffect, useMemo, useRef } from 'react';
import { Button, Col, Form, Input, notification, Row, Select, Space, Tag, Typography } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AppSelect } from '../../../../constants/appSelect';
import { CloseOutlined, ContainerOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { getAllVideosRequest } from '../../../../services/lichvietvideo/commonServiceRequest';
import {
  getBlockVideoInfoRequest,
  updateBlockVideoRequest,
} from '../../../../services/lichvietvideo/blockVideoServiceRequest';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { SHOW_IN_TAB_ID, SHOW_IN_TAB_OPTIONS } from '../config';

const { Text, Title } = Typography;

const UpdateBlockVideo = () => {
  const navigate = useNavigate();
  const { blockVideoId } = useParams();
  const [form] = Form.useForm();
  const watchVideoIds = Form.useWatch('video_ids', form);
  const listVideoRef = useRef(null);

  const { data: blockVideoInfo } = useQuery({
    queryKey: ['blockVideoInfo', blockVideoId],
    queryFn: () => getBlockVideoInfoRequest({ id: blockVideoId }),
  });

  useEffect(() => {
    if (blockVideoInfo) {
      form.setFieldsValue({
        ...blockVideoInfo,
        video_ids: blockVideoInfo?.videos?.map((item) => {
          return { video_id: item?.id };
        }),
      });
    }
  }, [form, blockVideoInfo]);

  // get data all video
  const { data: dataAllVideos } = useQuery({
    queryKey: ['dataAllVideos'],
    queryFn: () => getAllVideosRequest(),
  });

  // convert list block video options
  const dataAllVideosOption = useMemo(() => {
    const selected = watchVideoIds?.map((item) => item?.video_id) || [];
    return dataAllVideos?.data
      ?.filter((item) => item?.status === 1)
      ?.map((item) => {
        return { label: item?.name, value: item?.id, disabled: selected?.includes(item?.id) };
      });
  }, [dataAllVideos, watchVideoIds]);

  // handle submit form
  const { mutate: mutateUpdateBlockVideo, isPending } = useMutation({
    mutationFn: updateBlockVideoRequest,
    onSuccess: async (data) => {
      if (data?.status === 1) {
        notification.success({
          message: 'Thêm mới khối video thành công!',
        });
        navigate(-1);
      } else {
        notification.error({
          message: data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!',
        });
      }
    },
    onError: (error) => {
      notification.error({
        message: error.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!',
      });
    },
  });

  const handleSubmitForm = (data) => {
    const dataSubmit = {
      id: blockVideoInfo?.id,
      name: data?.name,
      description: data?.description,
      video_ids: data?.video_ids ? data?.video_ids?.map((item) => item?.video_id) : [],
      status: data?.status,
      link: null,
      sort_config: data?.sort_config,
      sort_place: data?.sort_place,
      show_in_tab: data?.show_in_tab,
    };
    mutateUpdateBlockVideo(dataSubmit);
  };

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;

    const { source, destination } = result;
    if (source.index === destination.index) return;

    const current = form.getFieldValue('video_ids');
    const reordered = reorder(current, source.index, destination.index);
    form.setFieldsValue({ video_ids: reordered });
  };

  const getSortPlaceOfVideoId = (index) => {
    const videoId = watchVideoIds?.[index]?.video_id;
    const sortPlace = blockVideoInfo?.videos?.find((item) => item?.id === videoId)?.sort_place;
    return sortPlace ? parseInt(sortPlace) : '';
  };

  return (
    <Form
      name="update-block-video"
      form={form}
      layout="vertical"
      className="mb-5"
      onFinish={handleSubmitForm}
    >
      <Row gutter={[16, 0]}>
        <Col xs={24}>
          <Title level={3}>Sửa khối video</Title>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="name"
            label={
              <Text>
                Tên khối video <span className="text-red-700">*</span>
              </Text>
            }
            rules={[{ required: true, message: 'Vui lòng nhập tên khối video' }]}
          >
            <Input placeholder="Nhập tên khối video..." />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="description"
            label={
              <Text>
                Mô tả <span className="text-red-700">*</span>
              </Text>
            }
            rules={[{ required: true, message: 'Vui lòng nhập mô tả khối video' }]}
          >
            <Input placeholder="Nhập mô tả khối video..." />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable-selects">
              {(provided) => (
                <Form.List name="video_ids">
                  {(fields, { add, remove }) => (
                    <>
                      <div className="flex flex-row justify-between items-center">
                        <Text className="mb-4">Danh sách video</Text>
                        <Form.Item>
                          <Button
                            type="primary"
                            onClick={() => {
                              add('', 0);
                              listVideoRef?.current?.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                          >
                            Thêm video
                          </Button>
                        </Form.Item>
                      </div>
                      <div
                        ref={(el) => {
                          provided.innerRef(el);
                          listVideoRef.current = el;
                        }}
                        {...provided.droppableProps}
                        className={`max-h-[428px] overflow-y-auto p-2 ${
                          fields?.length > 0 ? 'border border-[#d9d9d9] rounded-lg mb-4' : ''
                        }`}
                      >
                        <>
                          {fields.map((field, index) => (
                            <Draggable key={field.key.toString()} draggableId={field.key.toString()} index={index}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  style={{
                                    background: snapshot.isDragging ? '#f0f5ff' : '#fff',
                                    padding: 16,
                                    marginBottom: 8,
                                    border: '1px solid #d9d9d9',
                                    borderRadius: 8,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'between',
                                    gap: 8,
                                    ...provided.draggableProps.style,
                                    cursor: 'grab',
                                  }}
                                >
                                  <Form.Item
                                    {...field}
                                    name={[field.name, 'video_id']}
                                    fieldKey={[field.fieldKey, 'video_id']}
                                    className="flex-1 min-w-0 mb-0"
                                    rules={[{ required: true, message: 'Vui lòng chọn video' }]}
                                  >
                                    <Select
                                      placeholder="Chọn video"
                                      options={dataAllVideosOption}
                                      style={{
                                        width: '100%',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                      }}
                                    />
                                  </Form.Item>

                                  <Button type="text" danger onClick={() => remove(index)}>
                                    <DeleteOutlined className="text-xl" />
                                  </Button>
                                  <div className="flex items-center justify-center min-w-12">
                                    <Tag className="w-full h-[22px] text-center">{getSortPlaceOfVideoId(index)}</Tag>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          ))}
                        </>
                      </div>
                    </>
                  )}
                </Form.List>
              )}
            </Droppable>
          </DragDropContext>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="status"
            label={
              <Text>
                Trạng thái <span className="text-red-700">*</span>
              </Text>
            }
            rules={[{ required: true, message: 'Vui lòng chọn trạng thái' }]}
            initialValue={1}
          >
            <Select
              showSearch
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              placeholder="Chọn trạng thái"
              options={AppSelect.STATUS}
              allowClear
            />
          </Form.Item>
        </Col>
        <Col xs={12}>
          <Form.Item
            name="show_in_tab"
            label={
              <Text>
                Hiển thị trong tab <span className="text-red-700">*</span>
              </Text>
            }
            rules={[{ required: true, message: 'Vui lòng chọn trạng thái' }]}
            initialValue={SHOW_IN_TAB_ID.SHOW}
          >
            <Select
              showSearch
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              placeholder="Chọn trạng thái"
              options={SHOW_IN_TAB_OPTIONS}
            />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Row justify="end">
            <Col>
              <Space>
                <Button htmlType="button" icon={<CloseOutlined />} onClick={() => navigate(-1)}>
                  Hủy bỏ
                </Button>
                <Button loading={isPending} htmlType="submit" type="primary" icon={<ContainerOutlined />}>
                  Lưu lại
                </Button>
              </Space>
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
  );
};

export default UpdateBlockVideo;
